import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'

import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardMedia from '@material-ui/core/CardMedia'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import Collapse from '@material-ui/core/Collapse'
import IconButton from '@material-ui/core/IconButton'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import ShareIcon from '@material-ui/icons/Share'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
// import MoreVertIcon from '@material-ui/icons/MoreVert'
import { yellow, deepOrange, blue } from '@material-ui/core/colors'
import moment from 'moment-timezone'
import 'moment/locale/fi'
import 'moment/locale/sv'
import 'moment/locale/en-gb'
import 'moment/locale/et'
// import BookmarkIcon from '@material-ui/icons/Bookmark';
// import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import BookmarkTwoToneIcon from '@material-ui/icons/BookmarkTwoTone';
import { useTranslation } from 'react-i18next'
import { AppconfigContext } from '../appconfig-context'
import { venueLogoUrl } from '../utils/venuelogo'
import { getPosterUrl } from './ViewUtils'
import { canCancelParticipation, canLeaveQueue } from '../utils/activitycancel'
// import { ModeComment } from '@material-ui/icons'


// A soft white fade along the top and bottom edges of the image area (picture or placeholder), so it
// blends into the card.
const EDGE_FADE = 'inset 0 22px 18px -10px rgba(255,255,255,1), inset 0 -22px 18px -10px rgba(255,255,255,1)'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 380,
    // maxHeight: 400,
    backgroundColor: 'rgba(253,253,253,0.8)',
  },
  // In the activities list the card fills its grid column, and the cards of a row are equally tall
  // with the buttons at the bottom; in the schedule popups it keeps its own width.
  fill: {
    maxWidth: 'none',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  grow: {
    flexGrow: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  contentBottom: {
    marginTop: 'auto',
  },
  participantsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4, // a little air between the pill and the deadline lines below
  },
  headerTitle: {
    lineHeight: 1.3,
  },
  headerSubtitle: {
    textTransform: 'capitalize',
  },
  disabledBox: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '0.8125rem',
    lineHeight: 1.4,
  },
  media: {
    height: 0,
    paddingTop: '56.25%', // 16:9
    boxShadow: EDGE_FADE,
  },
  // the same 16:9 area for an activity without an image, so every card has the same shape
  placeholder: {
    position: 'relative',
    paddingTop: '56.25%',
    backgroundColor: '#eceff1',
    boxShadow: EDGE_FADE,
  },
  placeholderLogo: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: '35%',
    maxWidth: '50%',
    objectFit: 'contain',
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  typeLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 4,
    backgroundColor: blue[500],
    color: 'white',
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },
  typeLogo: {
    height: 16,
    marginRight: 6,
    borderRadius: 2,
  },
  // the sport next to the type: the same shape, outlined, so the two read as different things
  sportLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 7px',
    borderRadius: 4,
    border: '1px solid ' + blue[500],
    color: blue[700],
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },
  // a slim row at the very top of the card: the type and the sport
  typeRow: {
    padding: '6px 10px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    minHeight: 34, // the same height with or without a label, so the images stay lined up
  },
  horizontalroot: {
    maxWidth: '100%',
    marginBottom: 30,
    boxShadow: 'inset 200px 180px 135px 0px rgba(255,255,255,1)',
  },
  horizontalroote: {
    maxWidth: '100%',
    marginBottom: 30,
    boxShadow: 'inset 200px 330px 135px 0px rgba(255,255,255,1)',
  },
  horizontalmedia: {
    height: '100%',
    // paddingLeft: '56.25%', // 16:9
    // boxShadow: 'inset -2px -20px 35px 12px rgba(255,255,255,1)',
    width: '100%',
    maxHeight: 150,
    objectFit: 'cover',
  },
}))

// The activity's type ('Valmennus', 'Peli', ...) as a small label at the top of the card, with the
// activity's own logo before it when it has one and a bookmark when it is pinned.
const TypeLabel = ({ text, classes, logo, pinned }) => (
  <span className={classes.typeLabel}>
    {logo ? <img src={logo} alt='' className={classes.typeLogo} /> : null}
    {text}
    {pinned ? <BookmarkTwoToneIcon style={{ color: deepOrange[500], fontSize: 16, marginLeft: 4 }} /> : null}
  </span>
)

const SignupText = ({ price, isexternal, externaltext }) => {
  if (isexternal) {
    return <span>{externaltext || 'Ilmoittaudu'}</span>
  } else if (price > 0) {
    return <span>Ilmoittaudu {price}€</span>
  } else {
    return <span>Ilmoittaudu</span>
  }
}


export default function ActivityCard({
  activity,
  horizontal,
  paymentAction,
  cancelAction,
  uid,
  shareClick,
  gtime,
  defaultExpanded = false,
  fill = false,
}) {
  let {
    name,
    poster,
    description,
    price,
    type,
    start,
    end,
    ingress,
    maxp,
    participants,
    logo,
    queue,
    showusers,
    hasqueue,
    sport,
    providerlogo,
    // displayName,
  } = activity
  const classes = useStyles()
  const appconfig = React.useContext(AppconfigContext)
  const [expanded, setExpanded] = React.useState(defaultExpanded)
  const [logoFailed, setLogoFailed] = React.useState(false)
  const { t } = useTranslation()

  if (!participants) participants = []
  if (!queue) queue = []

  const hasEntry = participants.find((x) => x.id === uid)
  const isInQueue = queue.find((x) => x.id === uid)
  const positionInQueue = isInQueue ? queue.findIndex((x) => x.id === uid) + 1 : null
  const isFull = participants.length >= maxp
  const hasLittleRoom = participants.length > Math.floor(maxp * 0.75) && participants.length < maxp
  // const canSignUp = !hasEntry && !isInQueue && !isFull
  // const canQueue = !hasEntry && !isInQueue && participants.length >= maxp
  const signUpStart = moment.utc(activity.entrystart, 'YYYY-MM-DDTHH:mm').subtract(moment().tz('Europe/Helsinki').utcOffset(), 'minutes')
  const signUpEnd = moment.utc(activity.entryend, 'YYYY-MM-DDTHH:mm').subtract(moment().tz('Europe/Helsinki').utcOffset(), 'minutes')
  const isSignUpStarted = gtime.isSameOrAfter(signUpStart)
  const isSignUpEnded = gtime.isSameOrAfter(signUpEnd)
  const signUpOpen = gtime.isSameOrAfter(signUpStart) && gtime.isBefore(signUpEnd)

  // console.log(
  //   'signUpOpen',
  //   signUpOpen,
  //   'canSignUp',
  //   canSignUp,
  //   'canQueue',
  //   canQueue,
  //   gtime.format(),
  //   moment.utc(activity.entryend, 'YYYY-MM-DDTHH:mm').format()
  // )

  // console.log('isEntry', isEntry, 'isQueue', isQueue)

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }


  // const buttonSignUpText = price > 0 ? 'Ilmoittaudu ' + price + '€' : 'Ilmoittaudu'
  const buttonCancelText = t('cancel_participation')

  // A disabled button that says why there is nothing to press, so the card never has an empty button row.
  const disabledButton = (text, style) => (
    <Button variant='contained' color='secondary' disabled style={style}>
      {text}
    </Button>
  )

  // Looks like a disabled button but reads as ordinary text, so a long message fits and is not
  // mistaken for a button label.
  const disabledBox = (text) => <span className={classes.disabledBox}>{text}</span>

  const actionButton = () => {
    // A participant cancels until cancellationend (or entryend); the queue can be left while sign-up is open.
    if ((hasEntry && canCancelParticipation(activity, gtime)) || (!hasEntry && isInQueue && canLeaveQueue(activity, gtime))) {
      return (
        <Button variant='contained' color='secondary' onClick={() => cancelAction(activity)}>
          {buttonCancelText}
        </Button>
      )
    }
    // signed up and can no longer cancel
    if (hasEntry) return disabledButton(t('signed_up'))
    if (signUpOpen) {
      if (!isFull) {
        return (
          <Button
            variant='contained'
            color={'primary'}
            style={hasLittleRoom ? { backgroundColor: yellow[400], color: 'black' } : {}}
            onClick={() => paymentAction(activity)}
          >
            <SignupText price={price} isexternal={activity.isexternal} externaltext={activity.externaltext} />
          </Button>
        )
      } else {
        if (hasqueue) {
          return (
            <Button
              variant='contained'
              color={'primary'}
              onClick={() => paymentAction(activity)}
              style={{ backgroundColor: '#a643c1', color: 'white' }}
            >
              {t('add_to_queue')}
            </Button>
          )
        } else {
          return disabledButton(t('event_full'), { backgroundColor: yellow[400], color: 'black' })
        }
      }
    } else {
      if (!isSignUpStarted) {
        // signup started but not open yet
        return disabledButton(t('entry_not_open_yet') + ' ' + moment.utc(activity.entrystart, 'YYYY-MM-DDTHH:mm').format('D.M. H:mm'))
      } else if (isSignUpEnded) {
        // signup not open any more
        return disabledBox(t('entry_closed'))
      } else {
        // signup open but no entry
        return disabledButton(t('entry_not_open'))
      }
    }
  }

  const imageUrl = getPosterUrl(poster, 300)
  const logoUrl = logoFailed ? null : venueLogoUrl(appconfig && appconfig.logos)
  const when = moment.utc(start).format('dd D.M. H:mm') + ' - ' + moment.utc(end).format('H:mm')
  const title = name.fi ? name.fi.replace(/Ryhmäliikunta \//gi, '') : ''

  return (
        <Card className={clsx(classes.root, fill && classes.fill)}>
          <div className={classes.typeRow}>
            {type || logo || activity.pinned ? <TypeLabel text={type} classes={classes} logo={logo} pinned={activity.pinned} /> : null}
            {sport ? <span className={classes.sportLabel}>{sport}</span> : null}
          </div>
          {/* The image first, so the images of a row line up whatever the name's length. */}
          {imageUrl ? (
            <CardMedia className={classes.media} image={imageUrl} title='' style={{ position: 'relative' }}>
              {providerlogo ? (
                <div style={{ position: 'absolute', top: 15, left: 10 }}>
                  <img src={providerlogo} alt='provider logo' style={{ height: 30, objectFit: 'contain' }} />
                </div>
              ) : null}
            </CardMedia>
          ) : (
            <div className={classes.placeholder}>
              {logoUrl ? <img src={logoUrl} alt='' className={classes.placeholderLogo} onError={() => setLogoFailed(true)} /> : null}
            </div>
          )}
          {/* The full name and date: they can wrap, the image above keeps the rows lined up. */}
          <CardHeader
            style={{ paddingBottom: 8, alignItems: 'flex-start' }}
            title={title}
            titleTypographyProps={{ variant: 'subtitle1', className: classes.headerTitle }}
            subheader={when}
            subheaderTypographyProps={{ variant: 'body2', className: classes.headerSubtitle }}
          />

          <CardContent className={clsx(fill && classes.grow, fill && classes.content)}>
            {ingress && ingress.fi ? (
              <Typography variant='body2' color='textPrimary' component='p' gutterBottom>
                {ingress.fi}
              </Typography>
            ) : null}
            {/* participants and deadlines sit at the bottom, just above the buttons, on every card of a row */}
            <div className={clsx(fill && classes.contentBottom)}>
            {!activity.isexternal && !activity.hideentrycount ? (
              <Typography variant='body2' color='textPrimary' component='div' className={classes.participantsRow}>
                <span>{participants.length + '/' + maxp} {t('participants')}</span>
                {hasEntry || isInQueue ? (
                  <Chip
                    size='small'
                    color={hasEntry ? 'primary' : 'secondary'}
                    label={hasEntry ? t('signed_up') : t('in_queue') + (positionInQueue ? ' (' + positionInQueue + '.)' : '')}
                  />
                ) : null}
              </Typography>
          ) : null}
          {signUpOpen ? (
          <Typography variant='caption' color='textSecondary' component='p'>
            {t('entry_final_time')} {moment.utc(activity.entryend, 'YYYY-MM-DDTHH:mm').format('D.M. H:mm')}
            </Typography>
          ) : null}
          {/* cancelling closes before sign-up: everyone should know before signing up. A later
              cancellationend has no effect (entryend closes cancelling too), so it is not shown */}
          {activity.cancellationend && (!activity.entryend || activity.cancellationend < activity.entryend) ? (
            <Typography variant='caption' color='textSecondary' component='p'>
              {t('cancellation_final_time')} {moment.utc(activity.cancellationend, 'YYYY-MM-DDTHH:mm').format('D.M. H:mm')}
            </Typography>
          ) : null}
            </div>
          </CardContent>
          <CardActions disableSpacing>
          {actionButton()}
          
            {shareClick ? (
              <IconButton aria-label='share' onClick={() => shareClick(activity)}>
                <ShareIcon />
              </IconButton>
            ) : null}
            <IconButton
              className={clsx(classes.expand, {
                [classes.expandOpen]: expanded,
              })}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label='show more'
            >
              <ExpandMoreIcon />
            </IconButton>
          </CardActions>
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <CardContent>
              <Typography paragraph>{description.fi}</Typography>
              {showusers ? (
                <Typography variant='overline' gutterBottom>
                  Ilmoittautuneet
                </Typography>
              ) : null}
              {showusers
                ? participants.map((p) => (
                    <div>
                      <Typography key={p.id} variant='caption' gutterBottom>
                        {p.firstname + ' ' + p.lastname}
                      </Typography>
                    </div>
                  ))
                : null}
            </CardContent>
          </Collapse>
        </Card>
    )
  // }
}
